"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../../contexts/useTheme";
import { SwitchIcon } from "../../Icon";
import { Styling, Template } from "src/types";
import { fontOptions, templates, colorOptions } from "./data";
import { FontOption } from "./Select";
import Select from "./Select";
import { HexColorPicker } from "react-colorful";
import Modal from "../../Modal";
import { useOutsideClick } from "../../hooks/useOutsideClick";

// Define types for better type safety
interface ThemeSettings {
  primaryColor: string;
  template: Template;
  fontFamily: string;
  fontLink: string | null;
  showCustomColorPicker: boolean;
}

const ThemeSwitcher = () => {
  const isDemo = process.env.NEXT_PUBLIC_DEMO_MODE === "true";
  const { setStyling, setTemplate, config } = useTheme();
  const [customColor, setCustomColor] = useState("");
  const Modal2Ref = useRef<HTMLInputElement>(null);
  // Combine related state into a single object for better maintainability
  const [settings, setSettings] = useState<ThemeSettings>({
    primaryColor: "#280b05",
    template: (config?.template as Template) || config?.template, // Match initial value with `templates` data
    fontFamily: config?.styling.fontFamily || "Arial",
    fontLink: null,
    showCustomColorPicker: false,
  });
  const [showModal, setShowModal] = useState(false);
  const [mounted, setMounted] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  // Ensure proper mounting for portal
  useEffect(() => {
    setMounted(true);

    // Only update template if config.template exists
    if (config?.template) {
      setSettings((prev) => ({
        ...prev,
        template: config.template as Template,
        fontFamily: config?.styling.fontFamily || "Arial",
        primaryColor: config?.styling.primaryColor || "#280b05",
      }));
    }

    return () => setMounted(false);
  }, [config?.template]);


  // Close modal on outside click
  useOutsideClick([modalRef, Modal2Ref], () => setShowModal(false));
  // Memoized function to update settings
  const updateSettings = useCallback((updates: Partial<ThemeSettings>) => {
    setSettings((prev) => ({ ...prev, ...updates }));
  }, []);
  // Handle applying theme settings
  const handleApply = useCallback(() => {
    const { primaryColor, fontFamily, template } = settings;
    // Apply styling and template to context
    setStyling({ primaryColor, h1Size: "40px", fontFamily } as Styling);
    setTemplate(template);
    setShowModal(false);
    // No need for dynamic <link> tags since @next/font handles font loading
  }, [settings, setStyling, setTemplate]);
  // Early return if not in demo mode
  if (!isDemo) return null;

  const uniqueColors = [
    ...new Set([...colorOptions, config?.styling.primaryColor.toUpperCase()]),
  ];



  return (
    <>
      <button
        aria-label="Open theme switcher"
        className="fixed text-themeColor cursor-pointer bottom-20 right-8 z-[120]"
        onClick={() => setShowModal(true)}
      >
        <SwitchIcon />
      </button>

      {/* Modal for Theme Customization */}
      {mounted &&
        showModal &&
        createPortal(
          <div
            onClick={() => setShowModal(false)}
            className="fixed inset-0 z-[120] bg-black bg-opacity-50 flex items-center justify-center"
            role="dialog"
            aria-modal="true"
            aria-labelledby="theme-switcher-title"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              ref={modalRef}
              className="bg-gray-900 sm:max-w-[90vw] max-w-2xl text-white rounded-lg shadow-xl p-6 w-full"
            >
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <h2 id="theme-switcher-title" className="text-xl font-semibold">
                  Configure Your Choice
                </h2>
                <button
                  aria-label="Close theme switcher"
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500 rounded"
                >
                  ✕
                </button>
              </div>

              {/* Template Selection */}
              <div className="mb-6">
                <label className="block text-sm text-gray-300 mb-2">
                  Choose Template
                </label>
                <div className="grid grid-cols-3 gap-4">
                  {templates.map((tpl) => (
                    <button
                      key={tpl.value}
                      className={`rounded overflow-hidden cursor-pointer border-2 transition-all duration-200 ${
                        settings.template === tpl.value
                          ? "border-purple-500"
                          : "border-transparent hover:border-gray-600"
                      }`}
                      onClick={() =>
                        updateSettings({ template: tpl.value as Template })
                      }
                      aria-label={`Select ${tpl.name} template`}
                    >
                      <img
                        src={tpl.imageUrl}
                        alt={tpl.name}
                        className="w-full h-40 object-cover rounded"
                        onError={(e) => {
                          e.currentTarget.src =
                            "https://via.placeholder.com/330x297?text=Image+Not+Found";
                        }}
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Font Family Dropdown */}
              <div className="mb-6">
                <label
                  htmlFor="font-select"
                  className="block text-sm text-gray-300 mb-2"
                >
                  Preferred Font Style
                </label>
                <Select
                  options={fontOptions}
                  value={
                    fontOptions.find(
                      (option) => option.value === settings.fontFamily
                    ) || (null as unknown as FontOption)
                  }
                  onChange={(option?: FontOption) => {
                    if (!option) return;
                    updateSettings({
                      fontFamily: option.value,
                      // If you used a previous approach for googleFont, you no longer need it.
                    });
                  }}
                  placeholder="Select a font"
                />
              </div>

              {/* Color Scheme */}
              <div className="mb-6">
                <label className="block text-sm text-gray-300 mb-2">
                  Color Scheme
                </label>
                <div className="flex flex-wrap gap-2">
                  {uniqueColors.map((color) => (
                    <button
                      key={color}
                      style={{ backgroundColor: color }}
                      className={`w-8 h-8 grid place-items-center border-none rounded-md border-2 transition-all duration-200 `}
                      onClick={() => updateSettings({ primaryColor: color })}
                      aria-label={`Select color ${color}`}
                    >
                      {settings.primaryColor === color && <CheckIcon />}
                    </button>
                  ))}
                  <button
                    className="w-8 h-8 rounded-md border-2 border-dashed border-gray-400 flex items-center justify-center text-gray-400 hover:border-gray-300 transition-all duration-200"
                    onClick={() =>
                      updateSettings({
                        showCustomColorPicker: !settings.showCustomColorPicker,
                      })
                    }
                    aria-label="Open custom color picker"
                  >
                    +
                  </button>
                </div>

                {customColor && (
                  <div
                    onClick={() => {
                      updateSettings({ primaryColor: customColor });
                    }}
                    style={{
                      backgroundColor: customColor,
                    }}
                    className="custom grid mt-1 rounded-md place-items-center size-8"
                  >
                    {settings.primaryColor === customColor && <CheckIcon />}
                  </div>
                )}

                {/* {settings.showCustomColorPicker && (
                  <div className="mt-2 bg-transparent rounded-md overflow-hidden w-8 aspect-square">
                    <input
                      type="color"
                      value={settings.primaryColor}
                      onChange={(e) =>
                        updateSettings({ primaryColor: e.target.value })
                      }
                      className="w-full h-full border-transparent border-none outline-none"
                      aria-label="Custom color picker"
                    />
                  </div>
                )} */}

                {/* {settings.showCustomColorPicker && (
                  <div className="mt-4">
                    <HexColorPicker
                      className="!w-full"
                      color={settings.primaryColor}
                      onChange={(color) => {
                        updateSettings({
                          primaryColor: color,
                        });
                      }}
                    />
                    <button
                      onClick={() =>
                        updateSettings({
                          showCustomColorPicker:
                            !settings.showCustomColorPicker,
                        })
                      }
                      className="bg-white py-3 text-black w-full rounded-md"
                    >
                      Select color
                    </button>
                  </div>
                )} */}
              </div>
              {/* Preview Button */}
              <div className="flex justify-end">
                <button
                  onClick={handleApply}
                  className="px-6 py-2 bg-[#0033C4] text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
                  aria-label="Apply theme settings"
                >
                  Preview
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      <Modal
        ref={Modal2Ref}
        setOpen={() => {
          updateSettings({
            showCustomColorPicker: !settings.showCustomColorPicker,
          });
        }}
        open={settings.showCustomColorPicker}
      >
        <div className="mt-4">
          <HexColorPicker
            className="!w-full"
            color={settings.primaryColor}
            onChange={(color) => {
              setCustomColor(color);
              updateSettings({
                primaryColor: color,
              });
            }}
          />
          <button
            style={{
              backgroundColor: customColor || "blue",
            }}
            onClick={() =>
              updateSettings({
                showCustomColorPicker: !settings.showCustomColorPicker,
              })
            }
            className=" mt-4 text-white py-3  w-full rounded-md"
          >
            Select color
          </button>
        </div>
      </Modal>
    </>
  );
};

export default ThemeSwitcher;

const CheckIcon = () => {
  return (
    <svg
      width="16"
      height="17"
      viewBox="0 0 16 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3.33325 8.53539L6.66659 11.8687L13.3333 5.20206"
        stroke="white"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
};
